import React, { createContext, useContext } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "@/utils/utils";
import Checkbox from "./checkbox";
import { omitProps } from "./omit-props";

const CHECKBOX_GROUP_SPACING = {
  0: "space-y-0",
  8: "space-y-2",
  16: "space-y-4"
} as const;

type CheckboxGroupSpacing = keyof typeof CHECKBOX_GROUP_SPACING;

interface CheckboxGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  color?: string;
  /** Space between items in px. */
  spacing?: CheckboxGroupSpacing;
  /** Stretch the group to its container's width. */
  fullWidth?: boolean;
}
const CheckboxGroupContext = createContext<{ color?: string }>({});

const CheckboxGroup = React.forwardRef<HTMLDivElement, CheckboxGroupProps>(
  ({ className, color, spacing = 8, fullWidth, children, ...props }, ref) => (
    <CheckboxGroupContext.Provider value={{ color }}>
      <div
        ref={ref}
        className={cn(
          "flex flex-col",
          CHECKBOX_GROUP_SPACING[spacing],
          fullWidth && "w-full",
          className
        )}
        role="group"
        {...props}
      >
        {children}
      </div>
    </CheckboxGroupContext.Provider>
  )
);
CheckboxGroup.displayName = "CheckboxGroup";

export interface CheckboxGroupItemProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    "onCheckedChange"
  > {
  label?: string | JSX.Element;
  onCheckedChange?: (checked: boolean) => void;
  variant?: "default" | "partial";
  isCircle?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  checkboxClassName?: string;
}

const CheckboxGroupItem = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxGroupItemProps
>((allProps, ref) => {
  const {
    label,
    id,
    onCheckedChange,
    checked,
    disabled,
    variant = "default",
    isCircle,
    containerClassName,
    labelClassName,
    checkboxClassName,
    ...props
  } = omitProps(allProps, "className");
  const context = useContext(CheckboxGroupContext);
  const isInGroup = Object.keys(context).length > 0;

  const handleContainerClick = (event: React.MouseEvent) => {
    if (disabled) return;
    event.stopPropagation();
    event.preventDefault();
    onCheckedChange?.(!checked);
  };

  const showDash = variant === "partial";
  const showLabel = !!label;

  const CheckboxComponent = (
    <div
      className={cn(
        // Wrapper: padding (Space/XXS = 4px), rounded-full, hover state
        "p-2 rounded-full flex items-center justify-center transition-colors duration-200 ease-in-out",
        !disabled && !checked && "hover:bg-surface-hover-strong",
        checkboxClassName
      )}
    >
      <Checkbox
        ref={ref}
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        variant={showDash ? "partial" : "default"}
        isCircle={isCircle}
        {...props}
      />
    </div>
  );

  const containerClasses = cn(
    "flex items-center gap-1 justify-start",
    disabled ? "cursor-not-allowed" : "cursor-pointer",
    containerClassName
  );

  const labelClasses = cn(
    showLabel && "text-sm font-normal leading-normal",
    checked && !disabled && "text-primary",
    checked && disabled && "text-disabled",
    !checked && !disabled && "text-primary",
    !checked && disabled && "text-disabled",
    disabled ? "cursor-not-allowed" : "cursor-pointer",
    labelClassName
  );

  return (
    <div
      className={containerClasses}
      onClick={isInGroup ? handleContainerClick : undefined}
    >
      {CheckboxComponent}
      {showLabel && label && (
        <label htmlFor={id} className={labelClasses}>
          {label}
        </label>
      )}
    </div>
  );
});
CheckboxGroupItem.displayName = "CheckboxGroupItem";

export { CheckboxGroup, CheckboxGroupItem };
